import { useState } from 'react'
import { useDispatch } from 'react-redux'

/**
 * Shared submit-guard for the 8 resource request forms (issue #388).
 *
 * Wraps "dispatch the create thunk, track isSubmitting while it's in
 * flight, and only run the success callback (typically the form's
 * `reset`) when the create actually succeeded" so a failed create leaves
 * the user's input in place next to the error toast, and a double-click
 * on the submit button while a create is in flight can't fire two
 * creates.
 *
 * Requires the dispatched thunk to resolve to `true` on success / `false`
 * on failure, per the `updateProposal` convention in automationReducer.js
 * -- `createResource` (resourceReducer.js), `createCourse`
 * (coursesReducer.js), `createVideo` (videosReducer.js), `createPodcast`
 * (podcastsReducer.js), and `createTerminology` (terminologyReducer.js)
 * are updated to this contract as part of the same issue #388 fix.
 *
 * @returns {{ isSubmitting: boolean, submit: (thunkAction: Function, onSuccess?: Function) => Promise<boolean> }}
 */
const useResourceSubmit = () => {
    const dispatch = useDispatch()
    const [isSubmitting, setIsSubmitting] = useState(false)

    const submit = async (thunkAction, onSuccess) => {
        setIsSubmitting(true)
        try {
            const ok = await dispatch(thunkAction)
            if (ok && onSuccess) {
                onSuccess()
            }
            return !!ok
        } finally {
            setIsSubmitting(false)
        }
    }

    return { isSubmitting, submit }
}

export default useResourceSubmit
